import React, { useMemo, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import {
  applySuggestedTemplates,
  suggestTplAgenda,
  type TplAiSuggestContext,
} from '../../services/tplAiSuggest';
import type { LstTemplate } from '../../types';
import type { LstTemplateInput } from '../../services/lstService';
import { LEAN_NIVEL_LABELS } from '../../types/leanLibrary';
import { resolveLeanNivelForUser } from '../../types/leanLibrary';

interface TplAiSuggestModalProps {
  open: boolean;
  onClose: () => void;
  context: TplAiSuggestContext;
  existingTemplates: LstTemplate[];
  onApply: (templates: Partial<LstTemplateInput>[]) => Promise<void>;
  applying?: boolean;
}

const selectCls =
  'h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700 outline-none focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40';

export const TplAiSuggestModal: React.FC<TplAiSuggestModalProps> = ({
  open,
  onClose,
  context,
  existingTemplates,
  onApply,
  applying,
}) => {
  const [complexity, setComplexity] = useState<TplAiSuggestContext['operationComplexity']>('media');

  const result = useMemo(() => {
    const ctx = { ...context, operationComplexity: complexity };
    return suggestTplAgenda(ctx);
  }, [context, complexity]);

  const toCreate = useMemo(
    () => applySuggestedTemplates(existingTemplates, result.templatesToCreate),
    [existingTemplates, result.templatesToCreate],
  );

  const nivel = resolveLeanNivelForUser(context.lstCargo, context.roleName);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-label="Fechar" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-[var(--bg-surface)]">
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <div>
            <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-violet-100 bg-violet-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700">
              <Sparkles size={12} />
              IA Assistente TPL
            </div>
            <h2 className="text-[15px] font-bold text-slate-900 dark:text-slate-100">Sugerir meu TPL</h2>
            <p className="mt-0.5 text-[11px] text-slate-500">
              {LEAN_NIVEL_LABELS[nivel]} · {context.subordinateCount} liderado(s)
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={16} />
          </button>
        </header>

        <div className="shrink-0 border-b border-slate-100 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-900/30">
          <label className="mb-1 block text-[10px] font-bold uppercase text-slate-400">Complexidade da operação</label>
          <select
            className={selectCls}
            value={complexity}
            onChange={(e) => setComplexity(e.target.value as TplAiSuggestContext['operationComplexity'])}
          >
            <option value="baixa">Baixa</option>
            <option value="media">Média</option>
            <option value="alta">Alta</option>
          </select>
          <p className="mt-2 text-[11px] text-slate-600 dark:text-slate-300">{result.summary}</p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <p className="mb-2 text-[10px] font-bold uppercase text-slate-400">
            {toCreate.length} rotina(s) sugerida(s) · {result.totalWeeklyMinutes} min/semana
          </p>
          <div className="space-y-2">
            {result.slots.map((slot) => {
              const exists = existingTemplates.some(
                (t) => t.title.toLowerCase() === slot.item.titulo.toLowerCase(),
              );
              return (
                <div
                  key={slot.item.id}
                  className={`rounded-lg border p-3 ${exists ? 'border-slate-200 opacity-50' : 'border-violet-200 bg-violet-50/30 dark:border-violet-800'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-100">{slot.item.titulo}</p>
                      <p className="text-[10px] text-slate-500">
                        {slot.suggestedTime} · Dia {slot.suggestedDay} · {slot.item.duracaoRecomendada} min
                      </p>
                    </div>
                    {exists && <span className="text-[9px] text-slate-400">Já existe</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <footer className="shrink-0 flex justify-end gap-2 border-t border-slate-100 p-4 dark:border-slate-800">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-[11px] text-slate-500 hover:bg-slate-100">
            Cancelar
          </button>
          <button
            type="button"
            disabled={applying || toCreate.length === 0}
            onClick={() => onApply(toCreate)}
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-40"
          >
            <Sparkles size={14} />
            {applying ? 'Aplicando…' : `Aplicar ${toCreate.length} rotina(s)`}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default TplAiSuggestModal;
